// common\src\other\DateBirthValidator.js

/**
 * Valida a idade mínima baseada na data de nascimento
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @param {number} minAge - Idade mínima em anos
 * @returns {Object} - { isValid: boolean, age: number, message: string }
 */
export const validateMinAge = (birthDate, minAge) => {
  if (!birthDate) {
    return {
      isValid: false,
      age: 0,
      message: 'Data de nascimento é obrigatória'
    };
  }

  const birth = new Date(birthDate);
  const today = new Date();
  
  // Valida se a data é válida
  if (isNaN(birth.getTime())) {
    return {
      isValid: false,
      age: 0,
      message: 'Data de nascimento inválida'
    };
  }

  // Valida se a data não é no futuro
  if (birth > today) {
    return {
      isValid: false,
      age: 0,
      message: 'Data de nascimento não pode ser no futuro'
    };
  }

  // Calcula a idade
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  // Ajusta a idade se ainda não fez aniversário este ano
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  const isValid = age >= minAge;
  
  return {
    isValid,
    age,
    message: isValid 
      ? `Idade válida: ${age} anos` 
      : `Idade mínima requerida: ${minAge} anos. Você tem ${age} anos.`
  };
};

/**
 * Valida idade para registro de customer (mínimo 12 anos)
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @returns {Object} - { isValid: boolean, age: number, message: string }
 */
export const validateCustomerAge = (birthDate) => {
  return validateMinAge(birthDate, 12);
};

/**
 * Valida idade para registro de driver (mínimo 18 anos)
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @returns {Object} - { isValid: boolean, age: number, message: string }
 */
export const validateDriverAge = (birthDate) => {
  return validateMinAge(birthDate, 18);
};

/**
 * Valida idade baseada no tipo de usuário
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @param {string} userType - Tipo de usuário: 'customer' ou 'driver'
 * @returns {Object} - { isValid: boolean, age: number, message: string }
 */
export const validateAgeByUserType = (birthDate, userType) => {
  if (userType === 'driver') {
    return validateDriverAge(birthDate);
  }
  return validateCustomerAge(birthDate);
};

/**
 * Formata a data para exibição
 * @param {string} dateString - Data no formato YYYY-MM-DD
 * @param {string} locale - Localidade para formatação (padrão: 'pt-BR')
 * @returns {string} - Data formatada
 */
export const formatBirthDate = (dateString, locale = 'pt-BR') => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  return date.toLocaleDateString(locale);
};

/**
 * Calcula a idade exata em anos, meses e dias
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @returns {Object} - { years: number, months: number, days: number }
 */
export const calculateExactAge = (birthDate) => {
  if (!birthDate) return { years: 0, months: 0, days: 0 };
  
  const birth = new Date(birthDate);
  const today = new Date();
  
  if (isNaN(birth.getTime())) return { years: 0, months: 0, days: 0 };
  
  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  let days = today.getDate() - birth.getDate();
  
  if (days < 0) {
    months--;
    // Obtém o último dia do mês anterior
    const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += lastMonth.getDate();
  }
  
  if (months < 0) {
    years--;
    months += 12;
  }
  
  return { years, months, days };
};

/**
 * Verifica se é maior de idade (18+)
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @returns {boolean}
 */
export const isAdult = (birthDate) => {
  const result = validateMinAge(birthDate, 18);
  return result.isValid;
};

/**
 * Verifica se é adolescente (12-17 anos)
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @returns {boolean}
 */
export const isTeenager = (birthDate) => {
  const adultResult = validateMinAge(birthDate, 18);
  const teenResult = validateMinAge(birthDate, 12);
  
  return teenResult.isValid && !adultResult.isValid;
};

/**
 * Valida se a data está em um formato válido (YYYY-MM-DD)
 * @param {string} dateString - Data para validar
 * @returns {boolean}
 */
export const isValidDateString = (dateString) => {
  if (!dateString) return false;
  
  // Verifica o formato YYYY-MM-DD
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;
  
  const date = new Date(dateString);
  return !isNaN(date.getTime()) && date.toISOString().split('T')[0] === dateString;
};

export default {
  validateMinAge,
  validateCustomerAge,
  validateDriverAge,
  validateAgeByUserType,
  formatBirthDate,
  calculateExactAge,
  isAdult,
  isTeenager,
  isValidDateString
};